import React, { useState, useMemo, useEffect, useContext } from 'react'
import ReactDOM from 'react-dom'
import { useQuery, useMutation } from '@apollo/client'
import styles from './StudentDashboard.module.scss'
import {
  STUDENT_LANGUAGES_QUERY, STUDENT_BRIEF_QUERY, STUDENT_WEAK_ITEMS_QUERY,
  STUDENT_LETTER_STATS_QUERY, STUDENT_DRILL_RESULTS_QUERY, STUDENT_TREE_QUERY,
  ASSIGN_LESSON_MUTATION, ADD_MEETING_WITH_CONTENT_MUTATION, ATTACH_LESSON_TO_TOPIC_MUTATION,
} from '../query/gqlQueries'
import UserContext from '../context/UserContext'
import { buildTopicLesson } from '../lessons/buildTopicLesson'
import { DrillMisses, drillModeLabel } from './DrillDetails'

import CloseIcon from '@mui/icons-material/Close'
import LightModeIcon from '@mui/icons-material/LightMode'
import DarkModeIcon from '@mui/icons-material/DarkMode'

// One student, one language: what they have been doing, what they keep getting
// wrong, where they are on the tree, and one tap to set the next lesson.
//
// Built for the ten minutes before a lesson — the brief across the top should
// answer "where are they?" without any scrolling or reading.
//
// Everything here is refused by the server unless this student has added you
// with your teaching code, and it is deliberately narrow: learning data for one
// language. No profile facts, no journal, no notes, no other language.
const THEME_KEY = 'lessonTheme'
const readTheme = () => { try { const t = localStorage.getItem(THEME_KEY); return t === 'light' || t === 'dark' ? t : 'dark' } catch { return 'dark' } }

const LEVELS = ['A0', 'A1', 'A2', 'B1', 'B2', 'C1', 'C2']
const lvlIdx = (l) => { const i = LEVELS.indexOf(String(l || '').toUpperCase()); return i < 0 ? LEVELS.length : i }

// "today" / "yesterday" / "5 days ago" — a teacher wants the gap, not a date.
const sinceLabel = (iso) => {
  if (!iso) return 'never'
  const t = new Date(iso)
  if (isNaN(t)) return 'never'
  const days = Math.floor((Date.now() - t.getTime()) / 86400000)
  if (days <= 0) return 'today'
  if (days === 1) return 'yesterday'
  if (days < 30) return `${days} days ago`
  const months = Math.round(days / 30)
  return months === 1 ? 'a month ago' : `${months} months ago`
}
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString(undefined, { day: 'numeric', month: 'short' }) : '')
const pctColor = (p) => (p >= 80 ? 'var(--accent, #6ee7b7)' : p >= 50 ? 'var(--amber, #fbbf24)' : 'var(--error, #f87171)')
const STATUS_WORD = { assigned: 'not started', submitted: 'waiting for your feedback', reviewed: 'reviewed' }

export default function StudentDashboard({ student, onClose, onOpenImport }) {
  const [theme, setTheme] = useState(readTheme)
  const toggleTheme = () => setTheme(t => {
    const next = t === 'dark' ? 'light' : 'dark'
    try { localStorage.setItem(THEME_KEY, next) } catch { /* private mode */ }
    return next
  })

  const [lang, setLang] = useState('')
  const [due, setDue] = useState('')
  const [msg, setMsg] = useState('')
  const [err, setErr] = useState('')
  const [assigning, setAssigning] = useState(null) // topic id being assigned
  const [openDrill, setOpenDrill] = useState(null) // shared set expanded to show what needed help

  const langsQ = useQuery(STUDENT_LANGUAGES_QUERY, { variables: { student }, skip: !student, fetchPolicy: 'cache-and-network', errorPolicy: 'all' })
  const languages = useMemo(() => (langsQ.data?.studentLanguages || []).filter(l => l && l.code), [langsQ.data])
  // Default to the student's first active language, once they arrive.
  useEffect(() => {
    if (lang || languages.length === 0) return
    setLang((languages.find(l => l.active) || languages[0]).code)
  }, [languages, lang])

  const on = !!student && !!lang
  const briefQ = useQuery(STUDENT_BRIEF_QUERY, { variables: { student, lang }, skip: !on, fetchPolicy: 'cache-and-network', errorPolicy: 'all' })
  const weakQ = useQuery(STUDENT_WEAK_ITEMS_QUERY, { variables: { student, lang, limit: 12 }, skip: !on, fetchPolicy: 'cache-and-network', errorPolicy: 'ignore' })
  const lettersQ = useQuery(STUDENT_LETTER_STATS_QUERY, { variables: { student, lang }, skip: !on, fetchPolicy: 'cache-and-network', errorPolicy: 'ignore' })
  const drillsQ = useQuery(STUDENT_DRILL_RESULTS_QUERY, { variables: { student, limit: 10 }, skip: !student, fetchPolicy: 'cache-and-network', errorPolicy: 'ignore' })
  const treeQ = useQuery(STUDENT_TREE_QUERY, { variables: { student, langCode: lang }, skip: !on, fetchPolicy: 'cache-and-network', errorPolicy: 'ignore' })
  const [assign] = useMutation(ASSIGN_LESSON_MUTATION)
  const [addMeeting] = useMutation(ADD_MEETING_WITH_CONTENT_MUTATION)
  const [attachLesson] = useMutation(ATTACH_LESSON_TO_TOPIC_MUTATION)
  const { username } = useContext(UserContext)
  const [building, setBuilding] = useState('')   // progress text while a lesson is written

  const brief = briefQ.data?.studentBrief
  const weak = weakQ.data?.studentWeakItems || []
  const letters = lettersQ.data?.studentLetterStats || []
  const drills = drillsQ.data?.studentDrillResults || []
  const topics = useMemo(() => treeQ.data?.studentTree || [], [treeQ.data])

  const doneSlugs = useMemo(() => new Set(topics.filter(t => t.completed).map(t => t.slug)), [topics])
  const byLevel = useMemo(() => {
    const groups = new Map()
    for (const t of topics) {
      const key = t.level || '—'
      if (!groups.has(key)) groups.set(key, [])
      groups.get(key).push(t)
    }
    return [...groups.entries()].sort((a, b) => lvlIdx(a[0]) - lvlIdx(b[0]))
  }, [topics])
  const locked = (t) => (t.requires || []).some(r => !doneSlugs.has(r))

  // The server refuses outright when the teaching link is missing; say so
  // plainly rather than showing an empty dashboard that looks like no progress.
  const denied = [langsQ.error, briefQ.error].find(e => e && /only see the progress|not yours to see|has not yet said|Say which language|Not signed in/i.test(e.message || ''))
  const backendOld = /Cannot query field|Unknown type/i.test(langsQ.error?.message || '')

  const doAssign = async (topic) => {
    setMsg(''); setErr(''); setAssigning(topic.id)
    try {
      await assign({ variables: { meetingId: topic.lessonMeetingId, students: [student], due: due || null } })
      setMsg(`Assigned “${topic.title}” to ${student}${due ? ` · due ${fmtDate(due)}` : ''}.`)
      briefQ.refetch()
    } catch (e) { setErr(e.message) }
    setAssigning(null)
  }

  // A topic with no lesson yet: write it, attach it to the topic so every
  // learner of this language gets the same one, then set it. Uses exactly the
  // builder the learner's Skill Tree uses, so the homework is the tree lesson
  // rather than a teacher-only variant.
  const createAndAssign = async (topic) => {
    setMsg(''); setErr(''); setAssigning(topic.id)
    setBuilding('Writing the lesson…')
    try {
      const { meetingId } = await buildTopicLesson({
        topic, code: lang, langName, username, personal: null, addMeeting,
        onProgress: (acc) => setBuilding(`Writing the lesson… (${acc.length} chars)`),
      })
      if (!meetingId) throw new Error('The lesson was written but could not be saved.')
      setBuilding('Attaching and assigning…')
      await attachLesson({ variables: { topicId: topic.id, meetingId } })
      await assign({ variables: { meetingId, students: [student], due: due || null } })
      setMsg(`Created “${topic.title}” and set it for ${student}${due ? ` · due ${fmtDate(due)}` : ''}.`)
      treeQ.refetch(); briefQ.refetch()
    } catch (e) {
      setErr((e.message || '').match(/Failed to fetch|Load failed/) ? 'Connection to the Mac Studio failed — the lesson writer needs it.' : (e.message || 'Could not create the lesson.'))
    }
    setBuilding(''); setAssigning(null)
  }

  const langName = languages.find(l => l.code === lang)?.name || lang

  return ReactDOM.createPortal(
    <div className={`${styles.wrap} ${styles[theme]}`}>
      <div className={styles.inner}>
        <div className={styles.top}>
          <div className={styles.titleGroup}>
            <div className={styles.brand}>Student</div>
            <h1 className={styles.name}>{student}</h1>
          </div>
          <div className={styles.topRight}>
            <button className={styles.iconBtn} onClick={toggleTheme} title={theme === 'dark' ? 'Light mode' : 'Dark mode'} aria-label="Toggle light or dark mode">
              {theme === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
            </button>
            <button className={styles.closeBtn} onClick={onClose} aria-label="Close"><CloseIcon /></button>
          </div>
        </div>

        {backendOld && <div className={styles.err}>The GraphQL server hasn't been redeployed with the teaching queries yet.</div>}
        {denied && <div className={styles.err}>{denied.message}</div>}

        {!denied && !backendOld && (
          <>
            {languages.length > 0 && (
              <div className={styles.langRow}>
                {languages.map(l => (
                  <button key={l.code} className={`${styles.langChip} ${l.code === lang ? styles.langOn : ''}`} onClick={() => setLang(l.code)}>
                    {l.name || l.code}{l.level ? ` · ${l.level}` : ''}
                  </button>
                ))}
              </div>
            )}
            {languages.length === 0 && !langsQ.loading && (
              <div className={styles.empty}>
                {student} has not said which language you teach them, so nothing is shared yet. Ask them to
                tick it under <strong>Homework → My teachers</strong>. You will then see their progress in
                that language only.
              </div>
            )}

            {/* ── the brief ── */}
            {brief && (
              <div className={styles.brief}>
                <div className={styles.stat}>
                  <div className={styles.statLabel}>Last practised</div>
                  <div className={styles.statValue}>{sinceLabel(brief.lastPracticedAt)}</div>
                  <div className={styles.statNote}>{brief.itemsPracticed} {brief.itemsPracticed === 1 ? 'item' : 'items'} in {langName}</div>
                </div>
                <div className={styles.stat}>
                  <div className={styles.statLabel}>Due for review</div>
                  <div className={`${styles.statValue} ${brief.dueNow > 40 ? styles.statWarn : ''}`}>{brief.dueNow}</div>
                  <div className={styles.statNote}>{brief.dueNow === 0 ? 'nothing waiting' : 'cards overdue'}</div>
                </div>
                <div className={styles.stat}>
                  <div className={styles.statLabel}>Skill tree</div>
                  <div className={styles.statValue}>{brief.topicsCompleted}<span style={{ color: 'var(--muted, #8a8a95)', fontSize: '1rem' }}> / {brief.topicsTotal}</span></div>
                  <div className={styles.statNote}>{brief.nextLevel ? `next topic is ${brief.nextLevel}` : brief.topicsTotal ? 'every topic done' : 'tree not seeded'}</div>
                </div>
                <div className={styles.stat}>
                  <div className={styles.statLabel}>Homework</div>
                  <div className={styles.statValue}>{brief.homeworkSubmitted}<span style={{ color: 'var(--muted, #8a8a95)', fontSize: '1rem' }}> / {brief.homeworkAssigned}</span></div>
                  <div className={styles.statNote}>
                    {brief.lastHomeworkTitle
                      ? <>“{brief.lastHomeworkTitle}” — {STATUS_WORD[brief.lastHomeworkStatus] || brief.lastHomeworkStatus}</>
                      : 'nothing set yet'}
                  </div>
                </div>
              </div>
            )}

            <div className={styles.actions}>
              <button className={styles.primaryBtn} onClick={onOpenImport}>Upload a document as homework</button>
              <label className={styles.sectionNote} htmlFor="dashDue">Due date for what you assign</label>
              <input id="dashDue" type="date" className={styles.ghostBtn} value={due} onChange={e => setDue(e.target.value)} style={{ colorScheme: theme }} />
              {building && <span className={styles.building}>{building}</span>}
              {!building && msg && <span className={styles.msg}>{msg}</span>}
              {err && <span className={styles.err}>{err}</span>}
            </div>

            {/* ── what they keep getting wrong ── */}
            <div className={styles.section}>
              <div className={styles.sectionHead}>
                <h2 className={styles.sectionTitle}>Getting wrong</h2>
                <span className={styles.sectionNote}>worst first, from their own practice</span>
              </div>
              {weakQ.loading && weak.length === 0 ? <div className={styles.loading}>Loading…</div>
                : weak.length === 0 ? <div className={styles.empty}>Nothing to show — they have not missed anything in {langName} yet, or have not practised.</div>
                  : (
                    <div className={styles.weakList}>
                      {weak.map(w => (
                        <div key={w.id} className={styles.weakRow}>
                          <div className={styles.weakText}>
                            <div className={styles.weakTerm}>{w.text}</div>
                            {w.translationText && <div className={styles.weakTrans}>{w.translationText}</div>}
                          </div>
                          <span className={styles.typeTag}>{w.itemType === 'Word' ? 'word' : 'sentence'}</span>
                          <div className={styles.weakMeta}>
                            <div className={styles.weakBar}><div className={styles.weakFill} style={{ width: `${Math.max(3, w.pct)}%`, background: pctColor(w.pct) }} /></div>
                            <span className={styles.weakPct} style={{ color: pctColor(w.pct) }}>{w.pct}%</span>
                            <span className={styles.weakCount}>{w.timesCorrect}/{w.timesPracticed} · {sinceLabel(w.lastPracticedAt)}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
            </div>

            {/* ── the alphabet board, when the language has one ── */}
            {letters.length > 0 && (
              <div className={styles.section}>
                <div className={styles.sectionHead}>
                  <h2 className={styles.sectionTitle}>Letters</h2>
                  <span className={styles.sectionNote}>amber = keeps going wrong</span>
                </div>
                <div className={styles.letterGrid}>
                  {[...letters]
                    .sort((a, b) => (b.wrong / Math.max(1, b.right + b.wrong)) - (a.wrong / Math.max(1, a.right + a.wrong)))
                    .map(l => {
                      const total = l.right + l.wrong
                      const acc = total ? l.right / total : 0
                      const weakLetter = total >= 2 && acc < 0.7
                      const strong = total >= 3 && acc >= 0.9
                      return (
                        <div key={l.letter} className={`${styles.letter} ${weakLetter ? styles.letterWeak : strong ? styles.letterStrong : ''}`}>
                          <div className={styles.letterChar}>{l.letter}</div>
                          <div className={styles.letterScore}>{l.right}/{total}</div>
                        </div>
                      )
                    })}
                </div>
              </div>
            )}

            {/* ── practice they sent you ── */}
            <div className={styles.section}>
              <div className={styles.sectionHead}>
                <h2 className={styles.sectionTitle}>Practice they shared</h2>
                <span className={styles.sectionNote}>sets they chose to send you</span>
              </div>
              {drills.length === 0
                ? <div className={styles.empty}>Nothing shared yet. They can send a finished set from Special Mode or Verb Lab when they reach the results.</div>
                : drills.map(d => (
                  <div key={d.id} className={styles.drillRow} style={{ flexDirection: 'column', alignItems: 'stretch', cursor: 'pointer' }}
                    onClick={() => setOpenDrill(o => (o === d.id ? null : d.id))}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem', flexWrap: 'wrap' }}>
                      <div className={styles.drillTitle}>
                        {d.title}{d.mode ? <span className={styles.drillWhen}> · {drillModeLabel(d.mode)}</span> : null}
                        {d.comment && <div className={styles.comment}>💬 {d.comment}</div>}
                      </div>
                      <span className={styles.drillPct} style={{ color: pctColor(d.pct) }}>{d.pct}%</span>
                      <span className={styles.drillWhen}>{d.perfect}/{d.total} first time · {fmtDate(d.sharedAt)}</span>
                      <span className={styles.drillWhen}>{openDrill === d.id ? '▾' : '▸'}</span>
                    </div>
                    {/* What needed help — the verbs, tenses and persons, or the cases. */}
                    {openDrill === d.id && (
                      <div style={{ marginTop: '0.6rem', cursor: 'default' }} onClick={e => e.stopPropagation()}>
                        <DrillMisses details={d.details} />
                      </div>
                    )}
                  </div>
                ))}
            </div>

            {/* ── their tree, with one-tap assigning ── */}
            <div className={styles.section}>
              <div className={styles.sectionHead}>
                <h2 className={styles.sectionTitle}>Their skill tree</h2>
                <span className={styles.sectionNote}>exactly what {student} sees in {langName} · assign any topic with a lesson</span>
              </div>
              {topics.length === 0
                ? <div className={styles.empty}>No tree has been seeded for {langName} yet.</div>
                : byLevel.map(([level, list]) => (
                  <div key={level} className={styles.levelBlock}>
                    <div className={styles.levelHead}>
                      <span className={styles.levelName}>{level}</span>
                      <span className={styles.levelCount}>{list.filter(t => t.completed).length}/{list.length} done</span>
                    </div>
                    <div className={styles.topicGrid}>
                      {list.map(t => (
                        <div key={t.id} className={`${styles.topic} ${t.completed ? styles.topicDone : ''} ${!t.completed && locked(t) ? styles.topicLocked : ''}`}>
                          <div className={styles.topicTitle}>{t.title}</div>
                          <div className={styles.topicFoot}>
                            <span className={styles.topicState}>
                              {t.completed ? 'done' : locked(t) ? 'locked' : 'open'}
                            </span>
                            {t.lessonMeetingId
                              ? (
                                <button className={styles.assignBtn} onClick={() => doAssign(t)} disabled={!!assigning}>
                                  {assigning === t.id ? 'Assigning…' : 'Assign'}
                                </button>
                              ) : (
                                <button className={styles.makeBtn} onClick={() => createAndAssign(t)} disabled={!!assigning}
                                  title="No lesson exists for this topic yet — write one and set it">
                                  {assigning === t.id ? 'Writing…' : '+ Create & assign'}
                                </button>
                              )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
            </div>
          </>
        )}
      </div>
    </div>,
    document.body
  )
}
